import { AlertCircle, RefreshCw, WifiOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent } from '@/components/ui';
import { ApiError, NetworkError, RateLimitError, AuthError } from '@/lib/api/client';

interface ApiErrorDisplayProps {
  /** The error to display */
  error: Error;
  /** Optional retry handler */
  onRetry?: () => void;
}

/**
 * Component to display API errors in a user-friendly way.
 * Handles different error types with appropriate messages.
 */
export function ApiErrorDisplay({ error, onRetry }: ApiErrorDisplayProps) {
  const { t } = useTranslation('errors');

  let icon = <AlertCircle className="h-5 w-5 text-destructive" />;
  let title = t('api.defaultTitle');
  let message = error.message;

  if (error instanceof NetworkError) {
    icon = <WifiOff className="h-5 w-5 text-destructive" />;
    title = t('api.network.title');
    message = t('api.network.message');
  } else if (error instanceof RateLimitError) {
    title = t('api.rateLimit.title');
    message = t('api.rateLimit.message', { seconds: error.retryAfter });
  } else if (error instanceof AuthError) {
    title = t('api.auth.title');
    message = t('api.auth.message');
  } else if (error instanceof ApiError) {
    if (error.code === 'UNEXPECTED_RESPONSE') {
      title = t('api.notFound.title');
      message = t('api.notFound.message');
    } else {
      switch (error.status) {
        case 400:
          title = t('api.badRequest');
          break;
        case 403:
          title = t('api.forbidden.title');
          message = t('api.forbidden.message');
          break;
        case 404:
          title = t('api.notFound.title');
          message = t('api.notFound.message');
          break;
        case 500:
          title = t('api.server.title');
          message = t('api.server.message');
          break;
        default:
          title = t('api.withStatus', { status: error.status });
      }
    }
  }

  return (
    <Card className="border-destructive/50 bg-destructive/5">
      <CardContent className="flex items-start gap-4 p-4">
        <div className="flex-shrink-0 pt-0.5">{icon}</div>
        <div className="flex-1 space-y-1">
          <p className="font-medium text-destructive">{title}</p>
          <p className="text-sm text-muted-foreground">{message}</p>
        </div>
        {onRetry && (
          <Button variant="outline" size="sm" onClick={onRetry} className="flex-shrink-0">
            <RefreshCw className="mr-2 h-4 w-4" />
            {t('common:button.retry')}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
